// ============================================================================
//  CommissionPlansPage.tsx — Gestion des plans de commission NexusCorp ERP
//  Bloc F — Création plans (FIXED_RATE / TIERED / BONUS_ON_TARGET),
//            paliers configurables, assignation commerciaux
// ============================================================================

import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, ChevronDown, ChevronUp, Users, Percent,
  TrendingUp, Award, Edit2, X, Check, Loader2, AlertCircle,
} from 'lucide-react'
import api from '@/lib/axios'
import { useAuthStore } from '../../stores/auth.store'

// ─── Types ───────────────────────────────────────────────────────────────────

type CommissionType = 'FIXED_RATE' | 'TIERED' | 'BONUS_ON_TARGET'

interface CommissionTier {
  id:         string
  fromAmount: number
  toAmount:   number | null
  rate:       number
  bonusAmount?: number | null
}

interface CommissionPlan {
  id:           string
  name:         string
  description?: string
  type:         CommissionType
  triggerEvent: string
  isActive:     boolean
  createdAt:    string
  tiers:        CommissionTier[]
  _count:       { assignments: number }
}

interface Employee {
  id:        string
  firstName: string
  lastName:  string
  jobTitle?: string
  matricule: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<CommissionType, { label: string; icon: any; color: string; desc: string }> = {
  FIXED_RATE:      { label: 'Taux fixe',       icon: Percent,   color: 'text-[var(--blue-d)] bg-[var(--blue-bg)]',   desc: 'Pourcentage fixe sur le montant' },
  TIERED:          { label: 'Paliers',          icon: TrendingUp, color: 'text-[var(--amber-text)] bg-[var(--amber-bg)]', desc: 'Taux croissant par tranches' },
  BONUS_ON_TARGET: { label: 'Bonus sur objectif', icon: Award,   color: 'text-[var(--green-text)] bg-[var(--green-bg)]', desc: 'Bonus si objectif atteint' },
}

const TRIGGER_LABELS: Record<string, string> = {
  OPPORTUNITY_WON:    'Opportunité gagnée',
  INVOICE_PAID:       'Facture encaissée',
  PAYMENT_RECEIVED:   'Paiement reçu',
  DEAL_CLOSED:        'Affaire clôturée',
}

const fmtK = (v: number) =>
  v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1_000 ? `${(v / 1_000).toFixed(0)}K` : String(v)

const Spinner = ({ sm }: { sm?: boolean }) => (
  <Loader2 className={`animate-spin ${sm ? 'w-3.5 h-3.5' : 'w-5 h-5'}`} />
)

// ─── Composant palier ─────────────────────────────────────────────────────────

function TierRow({
  tier, idx, onUpdate, onRemove,
}: {
  tier: { fromAmount: string; toAmount: string; rate: string }
  idx:  number
  onUpdate: (idx: number, field: string, val: string) => void
  onRemove: (idx: number) => void
}) {
  return (
    <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
      <div>
        <label className="text-[10px] text-[var(--tx3)] mb-0.5 block">Dès (XOF)</label>
        <input
          type="number" value={tier.fromAmount} min="0" placeholder="0"
          onChange={e => onUpdate(idx, 'fromAmount', e.target.value)}
          className="w-full bg-white border border-[var(--bd)] rounded-lg px-2 py-1.5 text-xs text-[var(--tx)] focus:outline-none focus:border-blue-500"
        />
      </div>
      <div>
        <label className="text-[10px] text-[var(--tx3)] mb-0.5 block">Jusqu'à (XOF)</label>
        <input
          type="number" value={tier.toAmount} min="0" placeholder="Illimité"
          onChange={e => onUpdate(idx, 'toAmount', e.target.value)}
          className="w-full bg-white border border-[var(--bd)] rounded-lg px-2 py-1.5 text-xs text-[var(--tx)] focus:outline-none focus:border-blue-500"
        />
      </div>
      <div>
        <label className="text-[10px] text-[var(--tx3)] mb-0.5 block">Taux (%)</label>
        <input
          type="number" value={tier.rate} min="0" max="100" step="0.01" placeholder="5"
          onChange={e => onUpdate(idx, 'rate', e.target.value)}
          className="w-full bg-white border border-[var(--bd)] rounded-lg px-2 py-1.5 text-xs text-[var(--tx)] focus:outline-none focus:border-blue-500"
        />
      </div>
      <button
        onClick={() => onRemove(idx)}
        className="mt-4 p-1 text-[var(--tx3)] hover:text-[var(--red-text)] transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

// ─── Modal création plan ──────────────────────────────────────────────────────

function CreatePlanModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name:         '',
    description:  '',
    type:         'FIXED_RATE' as CommissionType,
    triggerEvent: 'OPPORTUNITY_WON',
  })
  const [tiers, setTiers] = useState([{ fromAmount: '0', toAmount: '', rate: '' }])
  const [fixedRate, setFixedRate] = useState('')
  const [error, setError] = useState('')

  const create = useMutation({
    mutationFn: (data: any) => api.post('/crm/commission-plans', data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['commission-plans'] }); onCreated() },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Erreur lors de la création'),
  })

  const addTier = () => setTiers(t => [...t, { fromAmount: '', toAmount: '', rate: '' }])
  const updateTier = (i: number, f: string, v: string) =>
    setTiers(t => t.map((tier, idx) => idx === i ? { ...tier, [f]: v } : tier))
  const removeTier = (i: number) => setTiers(t => t.filter((_, idx) => idx !== i))

  const handleSubmit = () => {
    if (!form.name.trim()) { setError('Le nom est obligatoire.'); return }

    let tiersPayload: any[] | undefined

    if (form.type === 'FIXED_RATE') {
      if (!fixedRate) { setError('Le taux est obligatoire.'); return }
      tiersPayload = [{ minAmount: 0, rate: parseFloat(fixedRate) }]
    } else if (form.type === 'TIERED') {
      if (tiers.some(t => !t.rate)) { setError('Chaque palier doit avoir un taux.'); return }
      tiersPayload = tiers.map(t => ({
        minAmount: parseFloat(t.fromAmount || '0'),
        maxAmount: t.toAmount ? parseFloat(t.toAmount) : undefined,
        rate:      parseFloat(t.rate),
      }))
    }

    create.mutate({ ...form, tiers: tiersPayload })
  }

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm z-[99999] flex items-start justify-center pt-16 px-4">
      <div className="nx-card w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--bd)]">
          <h2 className="text-sm font-bold text-[var(--tx)]">Nouveau plan de commission</h2>
          <button onClick={onClose} className="text-[var(--tx3)] hover:text-[var(--tx)]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Nom */}
          <div>
            <label className="text-xs text-[var(--tx3)] mb-1 block">Nom du plan *</label>
            <input
              value={form.name} placeholder="ex: Paliers commerciaux Q1 2026"
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs text-[var(--tx3)] mb-1 block">Description</label>
            <textarea
              value={form.description} rows={2} placeholder="Optionnel"
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] resize-none focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Type */}
          <div>
            <label className="text-xs text-[var(--tx3)] mb-2 block">Type de commission *</label>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(TYPE_CONFIG) as CommissionType[]).map(t => {
                const cfg = TYPE_CONFIG[t]
                const Icon = cfg.icon
                return (
                  <button
                    key={t}
                    onClick={() => setForm(f => ({ ...f, type: t }))}
                    className={`p-2.5 rounded-lg border text-left transition-colors ${
                      form.type === t ? 'border-blue-500 bg-[var(--blue-bg)]' : 'border-[var(--bd)] hover:border-[var(--bd2)]'
                    }`}
                  >
                    <Icon className={`w-4 h-4 mb-1 ${form.type === t ? 'text-[var(--blue-d)]' : 'text-[var(--tx3)]'}`} />
                    <p className={`text-[10px] font-medium ${form.type === t ? 'text-[var(--tx)]' : 'text-[var(--tx3)]'}`}>{cfg.label}</p>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Déclencheur */}
          <div>
            <label className="text-xs text-[var(--tx3)] mb-1 block">Déclencheur *</label>
            <select
              value={form.triggerEvent}
              onChange={e => setForm(f => ({ ...f, triggerEvent: e.target.value }))}
              className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
            >
              {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          {/* Taux fixe */}
          {form.type === 'FIXED_RATE' && (
            <div>
              <label className="text-xs text-[var(--tx3)] mb-1 block">Taux de commission (%) *</label>
              <input
                type="number" value={fixedRate} min="0" max="100" step="0.01" placeholder="ex: 5"
                onChange={e => setFixedRate(e.target.value)}
                className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
              />
            </div>
          )}

          {/* Paliers */}
          {form.type === 'TIERED' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-[var(--tx3)]">Paliers *</label>
                <button
                  onClick={addTier}
                  className="text-[10px] text-[var(--blue-d)] hover:text-blue-300 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Ajouter palier
                </button>
              </div>
              <div className="space-y-2">
                {tiers.map((tier, i) => (
                  <TierRow key={i} tier={tier} idx={i} onUpdate={updateTier} onRemove={removeTier} />
                ))}
              </div>
              <p className="text-[10px] text-[var(--tx4)] mt-1">Laisser "Jusqu'à" vide pour le dernier palier (illimité)</p>
            </div>
          )}

          {/* Erreur */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--red-bg)] border border-[rgba(239,68,68,0.25)] rounded-lg">
              <AlertCircle className="w-4 h-4 text-[var(--red-text)] shrink-0" />
              <p className="text-xs text-[var(--red-text)]">{error}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[var(--bd)]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Annuler</button>
          <button
            onClick={handleSubmit}
            disabled={create.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            {create.isPending ? <Spinner sm /> : <Check className="w-3.5 h-3.5" />}
            Créer le plan
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal assignation employé ─────────────────────────────────────────────────

function AssignModal({ plan, onClose }: { plan: CommissionPlan; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState('')
  const [success, setSuccess] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const { data: employees = [] } = useQuery<Employee[]>({
    queryKey: ['employees-search', search],
    queryFn:  () => api.get('/hr/employees', { params: { search, limit: 20 } })
      .then(r => (r.data.data ?? r.data).items ?? r.data.data ?? []),
  })

  const assign = useMutation({
    mutationFn: (employeeId: string) =>
      api.post(`/crm/commission-plans/${plan.id}/assign/${employeeId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-plans'] })
      queryClient.invalidateQueries({ queryKey: ['commissions'] })
      setSuccess(true)
      setTimeout(onClose, 1200)
    },
    onError: (err: any) => {
      // Afficher l'erreur réelle au lieu d'un échec silencieux
      const msg = err?.response?.data?.message ?? 'Échec de l\'assignation du plan. Réessayez.'
      setErrorMsg(typeof msg === 'string' ? msg : 'Erreur inconnue lors de l\'assignation.')
    },
  })

  const filtered = employees.filter(e =>
    `${e.firstName} ${e.lastName} ${e.matricule}`.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm z-[99999] flex items-center justify-center px-4">
      <div className="nx-card w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--bd)]">
          <div>
            <h2 className="text-sm font-bold text-[var(--tx)]">Assigner le plan</h2>
            <p className="text-[10px] text-[var(--tx3)] mt-0.5">{plan.name}</p>
          </div>
          <button onClick={onClose} className="text-[var(--tx3)] hover:text-[var(--tx)]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <input
            value={search} placeholder="Rechercher un employé..."
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
          />

          <div className="max-h-48 overflow-y-auto space-y-1">
            {filtered.map(emp => (
              <button
                key={emp.id}
                onClick={() => setSelectedId(emp.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  selectedId === emp.id ? 'bg-[var(--blue)]/20 border border-blue-500/30' : 'hover:bg-[var(--surface-2)]'
                }`}
              >
                <div className="w-7 h-7 rounded-full bg-[var(--surface-2)] flex items-center justify-center text-xs text-[var(--tx2)] shrink-0">
                  {emp.firstName[0]}{emp.lastName[0]}
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-[var(--tx)] truncate">{emp.firstName} {emp.lastName}</p>
                  <p className="text-[10px] text-[var(--tx3)]">{emp.jobTitle ?? emp.matricule}</p>
                </div>
                {selectedId === emp.id && <Check className="w-3.5 h-3.5 text-[var(--blue-d)] ml-auto shrink-0" />}
              </button>
            ))}
            {filtered.length === 0 && search.length > 0 && (
              <p className="text-xs text-[var(--tx3)] text-center py-4">Aucun employé trouvé</p>
            )}
          </div>

          {success && (
            <div className="flex items-center gap-2 p-2 bg-[var(--green-bg)] border border-[rgba(16,185,129,0.25)] rounded-lg">
              <Check className="w-4 h-4 text-[var(--green-text)]" />
              <p className="text-xs text-[var(--green-text)]">Plan assigné avec succès</p>
            </div>
          )}

          {errorMsg && !success && (
            <div className="flex items-start gap-2 p-2 bg-[#FEF2F2] border border-red-200 rounded-lg">
              <X className="w-4 h-4 text-[#DC2626] mt-0.5 shrink-0" />
              <p className="text-xs text-[#DC2626]">{errorMsg}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-[var(--bd)]">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Annuler</button>
          <button
            onClick={() => selectedId && assign.mutate(selectedId)}
            disabled={!selectedId || assign.isPending}
            className="flex items-center gap-2 px-3 py-1.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-40 text-white text-sm rounded-lg"
          >
            {assign.isPending ? <Spinner sm /> : <Users className="w-3.5 h-3.5" />}
            Assigner
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Plan Card ────────────────────────────────────────────────────────────────

function PlanCard({ plan, onAssign }: { plan: CommissionPlan; onAssign: (p: CommissionPlan) => void }) {
  const [expanded, setExpanded] = useState(false)
  const cfg = TYPE_CONFIG[plan.type]
  const Icon = cfg.icon

  return (
    <div className="nx-card overflow-hidden hover:border-[var(--bd)] transition-colors">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-lg ${cfg.color.split(' ')[1]}`}>
              <Icon className={`w-4 h-4 ${cfg.color.split(' ')[0]}`} />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-[var(--tx)] truncate">{plan.name}</h3>
              {plan.description && (
                <p className="text-[10px] text-[var(--tx3)] truncate mt-0.5">{plan.description}</p>
              )}
            </div>
          </div>
          <button
            onClick={() => onAssign(plan)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[var(--blue)]/20 hover:bg-[var(--blue)]/30 border border-blue-500/30 rounded-lg text-[10px] text-[var(--blue-d)] shrink-0 transition-colors"
          >
            <Users className="w-3 h-3" /> Assigner
          </button>
        </div>

        <div className="flex items-center gap-3 mt-3">
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cfg.color.split(' ')[1]} ${cfg.color.split(' ')[0]} border-current/20`}>
            {cfg.label}
          </span>
          <span className="text-[10px] text-[var(--tx3)]">
            {TRIGGER_LABELS[plan.triggerEvent] ?? plan.triggerEvent}
          </span>
          <span className="text-[10px] text-[var(--tx4)] ml-auto">
            <Users className="w-3 h-3 inline mr-0.5" />
            {plan._count.assignments} commercial{plan._count.assignments !== 1 ? 'x' : ''}
          </span>
        </div>
      </div>

      {/* Paliers (expandable) */}
      {plan.tiers.length > 0 && (
        <>
          <button
            onClick={() => setExpanded(e => !e)}
            className="w-full flex items-center justify-between px-4 py-2 border-t border-[var(--bd)] text-[10px] text-[var(--tx3)] hover:text-[var(--tx2)] transition-colors"
          >
            <span>{plan.tiers.length} palier{plan.tiers.length > 1 ? 's' : ''}</span>
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {expanded && (
            <div className="px-4 pb-3 space-y-1.5">
              {plan.tiers.map((tier, i) => (
                <div key={tier.id} className="flex items-center justify-between text-xs">
                  <span className="text-[var(--tx3)]">
                    {fmtK(tier.fromAmount)} XOF
                    {tier.toAmount ? ` → ${fmtK(tier.toAmount)} XOF` : ' et +'}
                  </span>
                  <span className="font-mono text-[var(--green-text)]">{Number(tier.rate)}%</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ============================================================================
//  PAGE PRINCIPALE
// ============================================================================

export default function CommissionPlansPage() {
  const { user }        = useAuthStore()
  const [showCreate, setShowCreate] = useState(false)
  const [assignTarget, setAssignTarget] = useState<CommissionPlan | null>(null)

  const canManage = user?.permissions?.includes('crm:configure')

  const { data: plans = [], isLoading } = useQuery<CommissionPlan[]>({
    queryKey: ['commission-plans'],
    queryFn:  () => api.get('/crm/commission-plans').then(r => r.data.data ?? r.data),
  })

  // ─── Stats rapides ──────────────────────────────────────────────────────────
  const totalAssignments = plans.reduce((s, p) => s + p._count.assignments, 0)
  const byType = {
    FIXED_RATE:      plans.filter(p => p.type === 'FIXED_RATE').length,
    TIERED:          plans.filter(p => p.type === 'TIERED').length,
    BONUS_ON_TARGET: plans.filter(p => p.type === 'BONUS_ON_TARGET').length,
  }

  return (
    <div className="p-6 space-y-6">

      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--tx)]">Plans de commission</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">
            Configurez les barèmes et assignez-les aux commerciaux
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white text-sm rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> Nouveau plan
          </button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="nx-card p-4">
          <p className="text-xs text-[var(--tx3)] mb-1">Plans actifs</p>
          <p className="text-2xl font-bold text-[var(--tx)]">{plans.length}</p>
        </div>
        <div className="nx-card p-4">
          <p className="text-xs text-[var(--tx3)] mb-1">Assignations</p>
          <p className="text-2xl font-bold text-[var(--tx)]">{totalAssignments}</p>
        </div>
        <div className="nx-card p-4">
          <p className="text-xs text-[var(--tx3)] mb-1">Taux fixe</p>
          <p className="text-2xl font-bold text-[var(--blue-d)]">{byType.FIXED_RATE}</p>
        </div>
        <div className="nx-card p-4">
          <p className="text-xs text-[var(--tx3)] mb-1">Paliers</p>
          <p className="text-2xl font-bold text-[var(--amber-text)]">{byType.TIERED}</p>
        </div>
      </div>

      {/* Liste des plans */}
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : plans.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Award className="w-12 h-12 text-[var(--tx2)] mb-3" />
          <p className="text-[var(--tx3)] font-medium">Aucun plan de commission</p>
          <p className="text-xs text-[var(--tx4)] mt-1 mb-4">
            Créez votre premier barème pour motiver vos commerciaux
          </p>
          {canManage && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white text-sm rounded-lg"
            >
              <Plus className="w-4 h-4" /> Créer un plan
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map(plan => (
            <PlanCard key={plan.id} plan={plan} onAssign={setAssignTarget} />
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <CreatePlanModal
          onClose={() => setShowCreate(false)}
          onCreated={() => setShowCreate(false)}
        />
      )}
      {assignTarget && (
        <AssignModal
          plan={assignTarget}
          onClose={() => setAssignTarget(null)}
        />
      )}
    </div>
  )
}
